// frontend/src/services/sportradarService.js
// Sportradar API integration
import { supabase } from './supabaseCore';

// NOTE: Sportradar credentials are now stored server-side in Edge Function secrets.
// The getSportradarToken function is no longer needed in the frontend.
// All credential-based operations go through the sportradar-search Edge Function.

// Search Sportradar Persons (via Edge Function - credentials stay server-side)
export const searchSportradar = async (query, offset = 0) => {
    try {
        if (!query || query.length < 3) return { data: [], error: null };

        const { data: result, error } = await supabase.functions.invoke('sportradar-search', {
            body: { action: 'search', query, offset }
        });

        if (error) {
            throw new Error(error.message || 'Sportradar search request failed');
        }

        return { data: result.data, error: null };

    } catch (error) {
        console.error('Sportradar Search Error:', error);
        return { data: null, error };
    }
};

// Legacy function - kept for backward compatibility but no longer does anything
// Token management is now handled server-side in the Edge Function
export const getSportradarToken = async () => {
    console.warn('getSportradarToken is deprecated - tokens are now managed server-side');
    return null;
};

// Link existing player to Sportradar Person
export const linkSportradarPlayer = async (playerId, sportradarPerson, photoUrlToUse) => {
    try {
        // First, fetch current player data to backup existing photo if we're going to overwrite
        let existingPhotoUrl = null;
        if (photoUrlToUse) {
            const { data: currentPlayer } = await supabase
                .from('event_uploads')
                .select('photo_url')
                .eq('id', playerId)
                .single();

            if (currentPlayer?.photo_url && currentPlayer.photo_url !== photoUrlToUse) {
                existingPhotoUrl = currentPlayer.photo_url;
            }
        }

        const updates = {
            person_id: sportradarPerson.personId,
            dob: sportradarPerson.dob || null,
            gender: sportradarPerson.gender || null,
            nationality: sportradarPerson.nationality || null,
            updated_at: new Date().toISOString()
        };

        if (sportradarPerson.images && sportradarPerson.images.length > 0) {
            updates.sportradar_photo_url = `${sportradarPerson.images[0].url}?size=RAW&format=webp`;
        }

        if (photoUrlToUse) {
            // Backup existing uploaded photo before overwriting
            if (existingPhotoUrl) {
                updates.uploaded_photo_url = existingPhotoUrl;
            }
            updates.photo_url = photoUrlToUse;
            updates.upload_status = true;
        }

        const { data, error } = await supabase
            .from('event_uploads')
            .update(updates)
            .eq('id', playerId)
            .select();

        if (error) throw error;
        return { data, error: null };
    } catch (error) {
        console.error('Link Player Error:', error);
        return { data: null, error };
    }
};

// Import new player from Sportradar
export const importSportradarPlayer = async (person, eventName, teamName, customName, storedPhotoUrl = null) => {
    try {
        // Use storedPhotoUrl (Supabase) if provided, otherwise fall back to external SR URL
        const externalPhotoUrl = person.images && person.images.length > 0
            ? `${person.images[0].url}?size=RAW&format=webp`
            : null;

        const photoUrl = storedPhotoUrl || externalPhotoUrl;

        const { data, error } = await supabase
            .from('event_uploads')
            .insert([{
                team: teamName,
                player_name: customName || person.nameFullLatin || person.nameFullLocal,
                event_name: eventName,
                upload_status: !!photoUrl,
                photo_url: photoUrl,
                person_id: person.personId,
                dob: person.dob || null,
                gender: person.gender || null,
                nationality: person.nationality || null,
                sportradar_photo_url: externalPhotoUrl // Always store the original SR URL for reference
            }])
            .select();

        if (error) throw error;
        return { data, error: null };
    } catch (error) {
        console.error('Import Player Error:', error);
        return { data: null, error };
    }
};

// Link an existing player record to a Sportradar Person ID (Self-Healing)
export const linkExistingPlayer = async (playerId, personId) => {
    try {
        const { error } = await supabase
            .from('event_uploads')
            .update({ person_id: personId })
            .eq('id', playerId);

        if (error) throw error;
        return { success: true };
    } catch (error) {
        console.error('Link Existing Player Error:', error);
        return { success: false, error };
    }
};

// Update Photo in Sportradar via Edge Function (bypasses CORS).
// invoke() attaches the signed-in user's JWT; the function rejects the anon key.
export const updateSportradarPhoto = async (personId, photoUrl) => {
    try {
        const { data, error } = await supabase.functions.invoke('sync-sportradar-photo', {
            body: { personId, photoUrl }
        });

        if (error) throw error;
        if (data?.error) throw new Error(data.error);

        return { success: true, newImageUrl: data.newImageUrl, error: null };

    } catch (error) {
        console.error('Update SR Photo Error:', error);
        return { success: false, error };
    }
};

// Fetch fresh photo URL from Sportradar
export const getSportradarPhoto = async (personId) => {
    try {
        const { data, error } = await supabase.functions.invoke('get-sportradar-photo', {
            body: { personId }
        });

        if (error) throw error;
        if (data?.error) throw new Error(data.error);

        return { success: true, imageUrl: data.imageUrl, error: null };

    } catch (error) {
        console.error('Get SR Photo Error:', error);
        return { success: false, error };
    }
};

// Download Sportradar photo and store in Supabase storage
// Uses get-sportradar-photo Edge Function with storage option
export const storeSportradarPhoto = async (personId, playerName, eventCode, teamName) => {
    try {
        const { data, error } = await supabase.functions.invoke('get-sportradar-photo', {
            body: {
                personId,
                // New parameters for storage
                storeToSupabase: true,
                playerName,
                eventCode,
                teamName
            }
        });

        if (error) throw error;
        if (data?.error) throw new Error(data.error);

        // If stored, returns storedUrl; otherwise returns imageUrl
        return {
            success: true,
            storedUrl: data.storedUrl || data.imageUrl,
            error: null
        };

    } catch (error) {
        console.error('Store SR Photo Error:', error);
        return { success: false, storedUrl: null, error };
    }
};
